import React, { useRef, useEffect, useState, useCallback } from "react";
import "./MattePaperCard.css";

/**
 * MattePaperCard - A 3D interactive card component with matte paper aesthetics
 * UPDATED: Smooth damping for fluid enter/exit, no stuttering
 *
 * @param {Object} props
 * @param {string} props.phase - Phase label (e.g., "PHASE 01")
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {Array<string>} props.tags - Array of tag strings
 * @param {number} props.maxTilt - Maximum tilt angle in degrees (default: 12)
 * @param {boolean} props.enableTilt - Enable/disable 3D tilt (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const MattePaperCard = ({
  phase,
  title,
  description,
  tags = [],
  maxTilt = 12,
  enableTilt = true,
  className = "",
}) => {
  const cardRef = useRef(null);
  const [tiltStyle, setTiltStyle] = useState({});
  const [isHovering, setIsHovering] = useState(false);
  const isTouchDevice = useRef(false);

  // Smooth animation state for damping
  const animationFrame = useRef(null);
  const currentRotation = useRef({ rotateX: 0, rotateY: 0, translateZ: 0 });
  const targetRotation = useRef({ rotateX: 0, rotateY: 0, translateZ: 0 });
  const isAnimating = useRef(false);

  useEffect(() => {
    // Detect touch devices on mount
    isTouchDevice.current =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  /**
   * SMOOTH ANIMATION LOOP: Uses RAF for buttery 60fps transitions
   * Implements damping to prevent stuttering at card boundaries
   */
  const animateToTarget = useCallback(() => {
    const dampingFactor = 0.15; // Lower = smoother but slower (0.1-0.2 is ideal)

    // Interpolate current values toward target values
    currentRotation.current.rotateX +=
      (targetRotation.current.rotateX - currentRotation.current.rotateX) *
      dampingFactor;
    currentRotation.current.rotateY +=
      (targetRotation.current.rotateY - currentRotation.current.rotateY) *
      dampingFactor;
    currentRotation.current.translateZ +=
      (targetRotation.current.translateZ - currentRotation.current.translateZ) *
      dampingFactor;

    const { rotateX, rotateY, translateZ } = currentRotation.current;

    // Calculate shadow based on current rotation (not target)
    const normalizedX = rotateY / maxTilt;
    const normalizedY = -rotateX / maxTilt;
    const shadowX = -normalizedX * 8;
    const shadowY = -normalizedY * 8;
    const shadowBlur = 20 + Math.abs(normalizedX * 10);

    setTiltStyle({
      transform: `
        perspective(1000px) 
        rotateX(${rotateX}deg) 
        rotateY(${rotateY}deg) 
        translateZ(${translateZ}px)
      `,
      boxShadow: `
        ${shadowX}px ${shadowY}px ${shadowBlur}px -2px rgba(0, 0, 0, 0.12),
        0 4px 6px -2px rgba(0, 0, 0, 0.08),
        0 2px 4px -1px rgba(0, 0, 0, 0.06)
      `,
      transition: "none",
    });

    // Continue animation if values haven't settled
    const isSettled =
      Math.abs(
        targetRotation.current.rotateX - currentRotation.current.rotateX
      ) < 0.01 &&
      Math.abs(
        targetRotation.current.rotateY - currentRotation.current.rotateY
      ) < 0.01 &&
      Math.abs(
        targetRotation.current.translateZ - currentRotation.current.translateZ
      ) < 0.01;

    if (!isSettled) {
      animationFrame.current = requestAnimationFrame(animateToTarget);
    } else {
      isAnimating.current = false;
    }
  }, [maxTilt]);

  /**
   * PHYSICS ENGINE: Calculate 3D tilt based on mouse position
   * Now sets TARGET values instead of applying directly
   */
  const handleMouseMove = (e) => {
    if (!enableTilt || isTouchDevice.current || !cardRef.current) return;

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();

    // 1. Get center point of the card
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // 2. Calculate mouse position relative to center
    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;

    // 3. Normalize to [-1, 1] range
    const normalizedX = (deltaX / rect.width) * 2;
    const normalizedY = (deltaY / rect.height) * 2;

    // 4. Calculate rotation angles
    const rotateY = normalizedX * maxTilt;
    const rotateX = -normalizedY * maxTilt;

    // 5. Calculate Z-axis lift
    const liftZ = Math.abs(normalizedX) + Math.abs(normalizedY) > 0.3 ? 15 : 0;

    // 6. Set target values (animation loop will smoothly interpolate)
    targetRotation.current = { rotateX, rotateY, translateZ: liftZ };

    // Start animation loop if not already running
    if (!isAnimating.current) {
      isAnimating.current = true;
      animationFrame.current = requestAnimationFrame(animateToTarget);
    }
  };

  /**
   * Reset card to neutral position when mouse leaves
   * Uses same smooth animation system
   */
  const handleMouseLeave = () => {
    setIsHovering(false);

    // Set target to neutral position
    targetRotation.current = { rotateX: 0, rotateY: 0, translateZ: 0 };

    // Start smooth return animation
    if (!isAnimating.current) {
      isAnimating.current = true;
      animationFrame.current = requestAnimationFrame(animateToTarget);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  /**
   * Touch device fallback - simple scale animation
   */
  const handleTouchStart = () => {
    if (isTouchDevice.current) {
      setTiltStyle({
        transform: "scale(1.02)",
        transition: "transform 0.2s ease",
      });
    }
  };

  const handleTouchEnd = () => {
    if (isTouchDevice.current) {
      setTiltStyle({
        transform: "scale(1)",
        transition: "transform 0.3s ease",
      });
    }
  };

  return (
    <div
      ref={cardRef}
      className={`matte-paper-card ${
        isHovering ? "is-hovering" : ""
      } ${className}`}
      style={tiltStyle}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Subtle paper grain texture overlay */}
      <div className="paper-grain" aria-hidden="true"></div>

      {/* Card content */}
      <div className="card-content">
        <span className="card-phase">{phase}</span>
        <h3 className="card-title">{title}</h3>
        <p className="card-description">{description}</p>

        {tags.length > 0 && (
          <div className="card-tags">
            {tags.map((tag, index) => (
              <span key={index} className="card-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MattePaperCard;
